<script>
const classByVariant = {
  primary: "btn--primary",
  secondary: "btn--secondary",
  tertiary: "btn--tertiary",
  destructive: "btn--destructive",
  link: "btn-link",
  text: "btn--text",
};

const sizes = ["small", "medium", "large"];
</script>

<script setup>
import Icon from "@/components/common/other/Icon.vue";
import LoaderIcon from "@/components/icons/LoaderIcon.vue";
import { computed, useSlots } from "vue";

const props = defineProps({
  variant: {
    type: String,
    default: "primary",
    validator: (value) => value in classByVariant,
  },
  size: {
    type: String,
    default: "medium",
    validator: (value) => sizes.includes(value),
  },
  isLoading: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  icon: { type: String, default: null },
  iconSource: { type: String, default: null },
  iconPosition: {
    type: String,
    default: "left",
    validator: (value) => ["left", "right"].includes(value),
  },
  label: {
    type: String,
    default: "",
  },
  as: {
    type: String,
    default: "button",
  },
  type: {
    type: String,
    default: "button",
    validator: (value) => ["button", "submit", "reset"].includes(value),
  },
});

const slots = useSlots();
const iconOnly = computed(() => !props.label && !slots.default);

const forwardProps = computed(() => {
  if (props.as === "button") {
    return {
      type: props.type,
      disabled: props.isLoading || props.disabled,
      "aria-busy": props.isLoading || null,
    };
  }
  if (props.disabled || props.isLoading) {
    return {
      "aria-disabled": "true",
      tabindex: -1,
    };
  }
  return {};
});
</script>

<template>
  <Component
    :is="as"
    class="btn d-inline-flex align-items-center gap-2 justify-content-center"
    :class="[
      classByVariant[variant],
      `btn--${size}`,
      { 'btn--disabled': disabled && !isLoading },
      { 'btn--icon-only': iconOnly },
    ]"
    v-bind="forwardProps"
  >
    <LoaderIcon v-if="isLoading" class="btn__spinner" />

    <span
      v-if="icon || $slots.icon"
      class="btn-icon"
      :class="[{ 'hide-text': isLoading, 'order-2': iconPosition === 'right' }]"
    >
      <slot name="icon">
        <Icon v-if="icon" :source="iconSource" :icon="icon" />
      </slot>
    </span>

    <span
      v-if="label || $slots.default"
      :class="['btn-label d-inline-block', { 'hide-text': isLoading }]"
    >
      <slot>{{ label }}</slot>
    </span>
  </Component>
</template>

<style scoped lang="scss">
.btn {
  --button-height: 40px;
  --button-icon-size: 20px;
  position: relative;
  height: var(--button-height);
  margin-bottom: 0;
  font-size: 0.875rem;
  text-transform: none;

  :deep(svg) {
    width: var(--button-icon-size);
    height: var(--button-icon-size);
  }

  .btn-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: var(--button-icon-color, currentColor);

    :deep(.material-icons),
    :deep(.material-symbols-outlined) {
      font-size: var(--button-icon-size);
    }
  }

  .btn-label {
    line-height: 1.2;
  }

  .hide-text {
    opacity: 0;
  }

  & :deep(i) {
    color: inherit;
  }

  &:focus-visible {
    outline: 1px solid var(--focus-ring-color);
    outline-offset: 1px;
    filter: drop-shadow(0 0 8px rgba(25, 78, 254, 0.4));
  }
}

.btn[aria-disabled="true"] {
  pointer-events: none;
}

.btn__spinner {
  position: absolute;
  inset: 0;
  margin: auto;
}

.btn.btn--small {
  --button-height: 32px;
  --button-icon-size: 16px;
  padding: 0 10px;
  border-radius: 6px;
  font-size: 0.75rem;

  .btn-label {
    line-height: 18px;
  }
}

.btn.btn--medium {
  --button-height: 40px;
  --button-icon-size: 20px;
  padding: 0 12px;
  border-radius: 8px;
  font-size: 0.875rem;

  .btn-label {
    line-height: 22px;
  }
}

.btn.btn--large {
  --button-height: 48px;
  --button-icon-size: 24px;
  padding: 0 16px;
  border-radius: 10px;
  font-size: 1rem;

  .btn-label {
    line-height: 24px;
  }
}

.btn.btn--icon-only {
  flex-shrink: 0;
  padding-inline: 0;
  width: var(--button-height);
}

.btn.btn--primary,
.btn.btn--secondary,
.btn.btn--tertiary,
.btn.btn--destructive,
.btn.btn--text {
  border: 1px solid transparent;
  background-image: none;
  font-weight: 700;

  &:disabled {
    opacity: 1;
  }

  &:active {
    opacity: 1;
  }
}

.btn.btn--primary {
  background-color: var(--forest-700);
  color: var(--bs-white);
  box-shadow: 0 2px 4px rgba(40, 84, 65, 0.1);

  &:hover {
    color: var(--bs-white);
    box-shadow: 0 6px 12px -2px rgba(40, 84, 65, 0.45);
  }

  &:active {
    background-color: var(--forest-900);
    box-shadow: 0 6px 12px -2px rgba(40, 84, 65, 0.45);
  }

  &.btn--disabled {
    background-color: var(--forest-200);
    box-shadow: none;
  }
}

.btn.btn--secondary {
  background-color: transparent;
  border-color: var(--forest-700);
  color: var(--forest-700);
  box-shadow: none;

  &:hover {
    border-color: var(--forest-600);
    color: var(--forest-600);
    box-shadow: none;
  }

  &:active {
    border-color: var(--forest-900);
    color: var(--forest-900);
    box-shadow: none;
  }

  &.btn--disabled {
    border-color: var(--gray-300);
    color: var(--gray-300);
  }
}

.btn.btn--tertiary {
  background-color: var(--gray-50);
  color: var(--gray-500);
  font-weight: 400;
  box-shadow: none;

  &:hover {
    color: var(--gray-600);
    box-shadow: 0 4px 10px -2px rgba(115, 115, 122, 0.15);
  }

  &:active {
    background-color: var(--gray-100);
    box-shadow: 0 2px 6px -1px rgba(115, 115, 122, 0.12);
  }

  &.btn--disabled {
    color: var(--gray-300);
    box-shadow: none;
  }
}

.btn.btn--destructive {
  background-color: transparent;
  border-color: var(--red-600);
  color: var(--red-600);
  box-shadow: none;

  &:hover {
    background-color: var(--red-600);
    color: var(--bs-white);
    box-shadow: 0 6px 12px -2px rgba(130, 47, 59, 0.45);
  }

  &:active {
    background-color: var(--red-700);
    border-color: var(--red-700);
    color: var(--bs-white);
    box-shadow: 0 6px 12px -2px rgba(130, 47, 59, 0.45);
  }

  &.btn--disabled {
    border-color: var(--gray-300);
    color: var(--gray-300);
  }
}

.btn.btn--text {
  background-color: transparent;
  color: var(--forest-700);
  box-shadow: none;

  &:hover {
    color: var(--forest-600);
    box-shadow: none;
  }

  &:active {
    color: var(--forest-900);
    box-shadow: none;
  }

  &:focus,
  &:focus-visible {
    box-shadow: none;
    filter: none;
  }

  &.btn--disabled {
    color: var(--gray-300);
  }
}

.btn.btn-link {
  height: auto;
  padding: 0;
  border: 0;
  border-radius: 0;
  font-weight: 600;
  color: var(--gray-800);
  text-decoration: underline;

  &:hover,
  &:focus {
    color: #000;
    text-decoration: underline;
  }
}
</style>
